import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';

const useOverlayStore = create()(
  devtools(
    persist(
      (set, get) => ({
        // State
        selectedOverlayIds: [],
        bushFireOverlays: [],
        fetchingOverlayIds: [],
        overlayOptions: [],
        isOverlayLoading: false,
        overlayError: null,
        addressData: null,

        // Actions
        setSelectedOverlayIds: (overlayIds) =>
          set({ selectedOverlayIds: overlayIds }, false, 'setSelectedOverlayIds'),

        setBushFireOverlays: (overlays) =>
          set({ bushFireOverlays: overlays }, false, 'setBushFireOverlays'),

        addBushFireOverlay: (overlay) =>
          set(
            (state) => {
              const filteredOverlays = state.bushFireOverlays.filter(
                (existingOverlay) => existingOverlay.overlayId !== overlay.overlayId
              );
              return {
                bushFireOverlays: [...filteredOverlays, overlay],
              };
            },
            false,
            'addBushFireOverlay'
          ),

        removeBushFireOverlay: (overlayId) =>
          set(
            (state) => ({
              bushFireOverlays: state.bushFireOverlays.filter(
                (overlay) => overlay.overlayId !== overlayId
              ),
            }),
            false,
            'removeBushFireOverlay'
          ),

        setFetchingOverlayIds: (ids) =>
          set({ fetchingOverlayIds: ids }, false, 'setFetchingOverlayIds'),

        addFetchingOverlayId: (overlayId) =>
          set(
            (state) => ({
              fetchingOverlayIds: [...state.fetchingOverlayIds, overlayId],
            }),
            false,
            'addFetchingOverlayId'
          ),

        removeFetchingOverlayId: (overlayId) =>
          set(
            (state) => ({
              fetchingOverlayIds: state.fetchingOverlayIds.filter((id) => id !== overlayId),
            }),
            false,
            'removeFetchingOverlayId'
          ),

        setOverlayOptions: (options) =>
          set({ overlayOptions: options }, false, 'setOverlayOptions'),

        setIsOverlayLoading: (loading) =>
          set({ isOverlayLoading: loading }, false, 'setIsOverlayLoading'),

        setOverlayError: (error) => set({ overlayError: error }, false, 'setOverlayError'),

        setAddressData: (data) => set({ addressData: data }, false, 'setAddressData'),

        // Handle overlay change (main function from MapView)
        handleOverlayChange: async (selectedOverlays, addressData, fetchOverlayByAddress) => {
          const {
            selectedOverlayIds,
            addFetchingOverlayId,
            removeFetchingOverlayId,
            removeBushFireOverlay,
          } = get();

          if (!addressData?.geometry?.rings) {
            return;
          }

          // Find newly added and removed overlay IDs
          const newOverlayIds = selectedOverlays.filter((id) => !selectedOverlayIds.includes(id));
          const removedOverlayIds = selectedOverlayIds.filter(
            (id) => !selectedOverlays.includes(id)
          );

          // Set new selected overlay IDs
          set({ selectedOverlayIds: selectedOverlays }, false, 'handleOverlayChange');

          // Fetch data for newly added overlays
          for (const overlayId of newOverlayIds) {
            try {
              await fetchOverlayByAddress(overlayId, addressData.geometry.rings);
            } catch (error) {
              console.error(`Failed to fetch overlay ${overlayId}:`, error);
            }
          }

          // Remove data for deselected overlays
          if (removedOverlayIds.length > 0) {
            removedOverlayIds.forEach((overlayId) => {
              removeBushFireOverlay(overlayId);
            });
          }
        },

        // Clear all overlay data
        clearOverlayData: () =>
          set(
            {
              selectedOverlayIds: [],
              bushFireOverlays: [],
              fetchingOverlayIds: [],
              isOverlayLoading: false,
              overlayError: null,
            },
            false,
            'clearOverlayData'
          ),

        // Reset overlay state
        resetOverlayState: () =>
          set(
            {
              selectedOverlayIds: [],
              bushFireOverlays: [],
              fetchingOverlayIds: [],
              overlayOptions: [],
              isOverlayLoading: false,
              overlayError: null,
              addressData: null,
            },
            false,
            'resetOverlayState'
          ),

        // Get overlay by ID
        getOverlayById: (overlayId) => {
          const { bushFireOverlays } = get();
          return bushFireOverlays.find((overlay) => overlay.overlayId === overlayId);
        },

        // Check if overlay is selected
        isOverlaySelected: (overlayId) => {
          const { selectedOverlayIds } = get();
          return selectedOverlayIds.includes(overlayId);
        },

        // Check if overlay is being fetched
        isOverlayFetching: (overlayId) => {
          const { fetchingOverlayIds } = get();
          return fetchingOverlayIds.includes(overlayId);
        },

        // Get overlay summary
        getOverlaySummary: () => {
          const { selectedOverlayIds, bushFireOverlays, overlayOptions } = get();
          return {
            totalSelected: selectedOverlayIds.length,
            totalLoaded: bushFireOverlays.length,
            totalAvailable: overlayOptions.length,
            overlayTypes: [...new Set(bushFireOverlays.map((overlay) => overlay.type))],
          };
        },
      }),
      {
        name: 'overlay-storage',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          selectedOverlayIds: state.selectedOverlayIds,
          bushFireOverlays: state.bushFireOverlays,
          overlayOptions: state.overlayOptions,
          addressData: state.addressData,
        }),
        version: 1,
        migrate: (persistedState, version) => {
          if (version < 1) {
            // Migration logic if needed
            return {
              ...persistedState,
              fetchingOverlayIds: [],
              isOverlayLoading: false,
              overlayError: null,
            };
          }
          return persistedState;
        },
      }
    ),
    {
      name: 'overlay-store',
    }
  )
);

export default useOverlayStore;
